import React, { useState, useEffect } from "react";
import "./calendarStyles.css";
import Spinner from "../loading/spinner";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import CustomToolbar from "./customToolbar";
import "../../../component/../App.css";
import MonthCalendar from "./monthCalendar";
import AddScheduleForDoctor from "./addScheduleForDoctor";
import EditSchedule from "./editSchedule";

const localizer = momentLocalizer(moment);

// Helper to get schedules from localStorage
const getSchedules = () => {
  return JSON.parse(localStorage.getItem("doctorSchedules")) || [];
};

const CalendarSchedule = () => {
  const [state, setState] = useState({
    loadingSpinner: false,
    collectScheduleData: [],
    selectedEvent: null,
    modalSelect: false,
    addScheduleButton: false,
  });

  const toggleState = (key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  // Load schedules on mount and after every change
  const refreshSchedules = () => {
    const schedules = getSchedules();
    toggleState("collectScheduleData", schedules);
  };

  useEffect(() => {
    refreshSchedules();
    // Listen for changes from other tabs (optional)
    const handleStorage = (e) => {
      if (e.key === "doctorSchedules") refreshSchedules();
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const getRandomColor = (i) => {
    const colors = [
      "#e0e7ff", "#c7d2fe", "#a5b4fc", "#818cf8",
      "#dbeafe", "#bfdbfe", "#93c5fd", "#60a5fa",
      "#e0f2fe", "#bae6fd", "#7dd3fc", "#38bdf8",
      "#fce7f3", "#fbcfe8", "#f9a8d4", "#f472b6",
    ];
    return colors[i % colors.length];
  };

  // Map stored schedules to calendar events
  const events = state.collectScheduleData.map((event, index) => ({
    start: new Date(`${event.stDate}T${event.startTime}`),
    end: new Date(`${event.endDate}T${event.endTime}`),
    id: event.id,
    color: getRandomColor(index),
    title: `${event.clinicName} - ${event.purpose}`,
    stDate: event.stDate,
    endDate: event.endDate,
    startTime: event.startTime,
    endTime: event.endTime,
    clinicName: event.clinicName,
    purpose: event.purpose,
    isShort: event.isShort || false,
  }));

  const handleEventSelect = (event) => {
    toggleState("selectedEvent", event);
    toggleState("modalSelect", true);
  };

  const closeModal = () => {
    toggleState("modalSelect", false);
    toggleState("selectedEvent", null);
    refreshSchedules(); // Refresh after edit/delete
  };

  const handleAddScheduleClose = () => {
    toggleState("addScheduleButton", false);
    refreshSchedules(); // Refresh after add
  };

  const CustomHeader = ({ date }) => {
    const day = moment(date).format("ddd");
    const dayNumber = moment(date).format("DD");
    return (
      <div className="text-center text-sm font-medium">
        <span className="block text-xs text-gray-500">{day}</span>
        <span className="text-indigo-700">{dayNumber}</span>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-4 h-full overflow-hidden">
        {/* Left Sidebar */}
        <div className="lg:w-72 xl:w-80 flex-shrink-0 h-full">
          <div className="bg-gray-50/80 rounded-xl p-4 border border-gray-200/60 shadow-sm h-full flex flex-col overflow-hidden">
            <div className="flex-1 min-h-0">
              <MonthCalendar />
            </div>
            <button
              onClick={() => toggleState("addScheduleButton", true)}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              <span>Add Schedule</span>
            </button>
          </div>
        </div>

        {/* Main Calendar */}
        <div className="flex-1 min-w-0 bg-white rounded-xl border border-gray-200/60 shadow-sm p-3 sm:p-4 overflow-hidden h-full">
          <Calendar
            localizer={localizer}
            startAccessor="start"
            events={events}
            endAccessor="end"
            style={{ height: "100%", width: "100%" }}
            eventPropGetter={(event) => ({
              style: {
                backgroundColor: event.color,
                color: "#1e293b",
                border: "none",
                borderRadius: "6px",
                padding: "2px 6px",
                fontSize: "0.75rem",
                fontWeight: "600",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              },
            })}
            onSelectEvent={handleEventSelect}
            views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
            components={{
              toolbar: CustomToolbar,
              month: { header: CustomHeader },
              week: { header: CustomHeader },
              day: { header: CustomHeader },
            }}
          />
        </div>
      </div>

      {state.loadingSpinner && <Spinner />}
      {state.modalSelect && (
        <EditSchedule
          reset={closeModal}
          event={state.selectedEvent}
          onUpdate={refreshSchedules}
        />
      )}
      {state.addScheduleButton && (
        <AddScheduleForDoctor
          reset={handleAddScheduleClose}
          updatedSchedule={null}
        />
      )}
    </>
  );
};

export default CalendarSchedule;